import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ko, enUS } from 'date-fns/locale';
import { lessonLogAPI, LessonLog, getYoutubeThumbnail } from '../../services/lessonLogAPI';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/pages/user/_lesson-logs.scss';

// URL을 링크로 변환하는 함수
const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="comment-link"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const LessonLogs: React.FC = () => {
  const { t, language } = useLanguage();
  const dateLocale = language === 'ENG' ? enUS : ko;
  const [logs, setLogs] = useState<LessonLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<LessonLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

  // 브라우저 뒤로가기(Android 시스템 백 버튼) 처리
  const handlePopState = useCallback(() => {
    setMobileView('list');
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handlePopState]);

  useEffect(() => {
    loadMyLogs();
  }, []);

  const loadMyLogs = async () => {
    setLoading(true);
    try {
      const response = await lessonLogAPI.getMyLogs({ limit: 50 });
      if (response.success) {
        setLogs(response.data || []);
        if (response.data && response.data.length > 0) {
          setSelectedLog(response.data[0]);
        }
      }
    } catch {
      toast.error(t('lesson_logs_load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogClick = async (log: LessonLog) => {
    setSelectedLog(log);
    setCommentText('');
    window.history.pushState({ view: 'detail' }, '');
    setMobileView('detail');

    // 읽음 처리
    try {
      await lessonLogAPI.markMyLogAsRead(log.id);
      // 로컬 상태 업데이트 (빨간 점 제거)
      setLogs(prevLogs => prevLogs.map(l => {
        if (l.id === log.id) {
          return {
            ...l,
            isReadByStudent: true,
            comments: l.comments?.map(c => ({
              ...c,
              isReadByStudent: c.authorType === 'teacher' ? true : c.isReadByStudent
            }))
          };
        }
        return l;
      }));
      // 사이드바 카운트 갱신 이벤트
      window.dispatchEvent(new CustomEvent('lessonLogRead'));
    } catch {
      // 오류 무시
    }
  };

  const handleAddComment = async () => {
    if (!selectedLog || !commentText.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await lessonLogAPI.addComment(selectedLog.id, commentText.trim());
      if (response.success) {
        toast.success(t('comment_added'));
        setCommentText('');
        const updatedLogs = logs.map(log => {
          if (log.id === selectedLog.id) {
            return {
              ...log,
              comments: [...(log.comments || []), response.data]
            };
          }
          return log;
        });
        setLogs(updatedLogs);
        setSelectedLog({
          ...selectedLog,
          comments: [...(selectedLog.comments || []), response.data]
        });
      }
    } catch {
      toast.error(t('comment_add_failed'));
    } finally {
      setSubmittingComment(false);
    }
  };

  // 댓글 수정 시작
  const handleStartEdit = (comment: { id: number; message: string }) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.message);
  };

  // 댓글 수정 취소
  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditCommentText('');
  };

  // 댓글 수정 저장
  const handleSaveEdit = async () => {
    if (!editingCommentId || !editCommentText.trim()) return;

    try {
      const response = await lessonLogAPI.updateComment(editingCommentId, editCommentText.trim());
      if (response.success) {
        toast.success(t('comment_edited'));
        // 로컬 상태 업데이트
        if (selectedLog) {
          const updatedLog = {
            ...selectedLog,
            comments: selectedLog.comments?.map(c =>
              c.id === editingCommentId ? { ...c, message: editCommentText.trim() } : c
            )
          };
          setSelectedLog(updatedLog);
          setLogs(prevLogs =>
            prevLogs.map(l => l.id === selectedLog.id ? updatedLog : l)
          );
        }
        handleCancelEdit();
      }
    } catch {
      toast.error(t('comment_edit_failed'));
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm(t('comment_delete_confirm'))) return;

    try {
      const response = await lessonLogAPI.deleteComment(commentId);
      if (response.success) {
        toast.success(t('comment_deleted'));
        // 로컬 상태 업데이트
        if (selectedLog) {
          const updatedLog = {
            ...selectedLog,
            comments: selectedLog.comments?.filter(c => c.id !== commentId)
          };
          setSelectedLog(updatedLog);
          setLogs(prevLogs =>
            prevLogs.map(l => l.id === selectedLog.id ? updatedLog : l)
          );
        }
      }
    } catch {
      toast.error(t('comment_delete_failed'));
    }
  };

  const thumbnail = selectedLog?.songYoutubeUrl ? getYoutubeThumbnail(selectedLog.songYoutubeUrl) : null;

  if (loading) {
    return (
      <div className="student-logs-page">
        <div className="page-loading">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className={`student-logs-page mobile-${mobileView}`}>
      {/* 상단 헤더 바 (데스크탑) */}
      <TeacherPageHeader title="수업 기록" subtitle={`총 ${logs.length}건`} />

      <div className="log-panels">
      {/* 수업 기록 목록 */}
      <div className="list-section">
        <div className="section-header">
          <span>기록 내역</span>
          <span className="count">{logs.length}</span>
        </div>
        <div className="log-list">
          {logs.length === 0 ? (
            <div className="empty-list">
              <p>{t('no_lesson_logs')}</p>
            </div>
          ) : (
            logs.map(log => {
              // 미확인 여부 체크 (수업기록 자체 또는 선생님 댓글)
              const hasUnreadTeacherComment = log.comments?.some(
                c => c.authorType === 'teacher' && c.isReadByStudent === false
              );
              const isUnread = log.isReadByStudent === false || hasUnreadTeacherComment;

              return (
                <div
                  key={log.id}
                  className={`log-item ${selectedLog?.id === log.id ? 'active' : ''} ${isUnread ? 'unread' : ''}`}
                  onClick={() => handleLogClick(log)}
                >
                  <div className="log-main">
                    <div className="log-date">
                      {format(new Date(log.lessonDate), 'M. d (E)', { locale: dateLocale })}
                      {log.lessonTime && ` · ${log.lessonTime.slice(0, 5)}`}
                    </div>
                    <div className="song">{log.song || '수업 기록'}</div>
                  </div>
                  {isUnread && <span className="unread-dot" />}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 상세 보기 */}
      <div className="detail-section">
        {!selectedLog ? (
          <div className="empty-detail">
            <p>{t('select_lesson')}</p>
          </div>
        ) : (
          <>
            {/* 헤더 */}
            <div className="detail-header">
              <button className="btn-back-mobile" onClick={() => window.history.back()}>←</button>
              <div>
                <span className="date">
                  {format(new Date(selectedLog.lessonDate), language === 'ENG' ? 'yyyy. M. d (E)' : 'yyyy. M. d (E)', { locale: dateLocale })}
                  {selectedLog.lessonTime && ` ${selectedLog.lessonTime.slice(0, 5)}`}
                </span>
                <h2>
                  {selectedLog.song || format(new Date(selectedLog.lessonDate), language === 'ENG' ? 'MMMM d, EEEE' : 'M월 d일 수업', { locale: dateLocale })}
                </h2>
              </div>
            </div>

            <div className="detail-body">
              {/* 곡 정보 */}
              {selectedLog.song && (
                <div className="info-row song-row">
                  <div className="row-label">{t('song_label')}</div>
                  <div className="row-content">
                    <span className="song-name">{selectedLog.song}</span>
                    {thumbnail && (
                      <a
                        href={selectedLog.songYoutubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="youtube-thumb"
                      >
                        <img src={thumbnail} alt="" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* 수업 내용 */}
              <div className="content-box">
                <div className="box-label">{t('lesson_content')}</div>
                <div className="box-content">
                  {selectedLog.content || '-'}
                </div>
              </div>

              {/* 과제 */}
              <div className="content-box homework-box">
                <div className="box-label">{t('homework')}</div>
                <div className="box-content">
                  {selectedLog.homework || '-'}
                </div>
              </div>

              {/* 댓글 */}
              <div className="comments-area">
                <div className="comments-header">
                  <span>{t('comments')}</span>
                  {selectedLog.comments && selectedLog.comments.length > 0 && (
                    <span className="count">{selectedLog.comments.length}</span>
                  )}
                </div>

                <div className="comments-list">
                  {selectedLog.comments && selectedLog.comments.length > 0 ? (
                    selectedLog.comments.map(comment => (
                      <div key={comment.id} className={`comment ${comment.authorType}`}>
                        <div className="comment-content">
                          {editingCommentId === comment.id ? (
                            <div className="comment-edit-form">
                              <input
                                type="text"
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveEdit();
                                  }
                                }}
                                autoFocus
                              />
                              <div className="edit-actions">
                                <button className="btn-cancel" onClick={handleCancelEdit}>
                                  {t('comment_cancel')}
                                </button>
                                <button
                                  className="btn-save"
                                  onClick={handleSaveEdit}
                                  disabled={!editCommentText.trim()}
                                >
                                  {t('comment_save')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p>{linkifyText(comment.message)}</p>
                          )}
                          <div className="comment-meta">
                            <span className="author">
                              {comment.authorType === 'teacher'
                                ? (comment.teacher?.name || t('teacher_default_name'))
                                : t('me')}
                            </span>
                            <span className="time">
                              {format(new Date(comment.createdAt), 'M.d HH:mm')}
                            </span>
                            {comment.authorType === 'student' && editingCommentId !== comment.id && (
                              <div className="comment-actions">
                                <button
                                  className="btn-edit"
                                  onClick={() => handleStartEdit(comment)}
                                >
                                  {t('comment_edit')}
                                </button>
                                <button
                                  className="btn-delete"
                                  onClick={() => handleDeleteComment(comment.id)}
                                >
                                  {t('comment_delete')}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-comments">{t('no_comments')}</div>
                  )}
                </div>

                <div className="comment-form">
                  <div className="input-wrapper">
                    <input
                      type="text"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder={t('comment_placeholder')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                    />
                    <button
                      className="youtube-icon"
                      onClick={() => {
                        const query = selectedLog?.song || t('drum_cover');
                        window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`, '_blank');
                      }}
                      title={t('search_on_youtube')}
                    >
                      <svg width="18" height="13" viewBox="0 0 24 18" fill="currentColor">
                        <path d="M23.5 3.5c-.3-1-1-1.8-2-2C19.6 1 12 1 12 1s-7.6 0-9.5.5c-1 .2-1.7 1-2 2C0 5.4 0 9 0 9s0 3.6.5 5.5c.3 1 1 1.8 2 2 1.9.5 9.5.5 9.5.5s7.6 0 9.5-.5c1-.2 1.7-1 2-2 .5-1.9.5-5.5.5-5.5s0-3.6-.5-5.5zM9.5 12.5v-7l6.4 3.5-6.4 3.5z"/>
                      </svg>
                    </button>
                  </div>
                  <button
                    className="send-btn"
                    onClick={handleAddComment}
                    disabled={!commentText.trim() || submittingComment}
                  >
                    {t('send')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default LessonLogs;
